import { type PaletteMode, type Theme, ThemeProvider } from "@mui/material";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

export type GetThemeFunctionType = (
	variant: PaletteMode,
	hspColor?: string,
	zIndexBase?: number,
) => Theme;

export type ColorSchemeOptions = {
	paletteMode: PaletteMode;
	hspColor: string | undefined;
};

type ThemeModeContextProps = {
	mode: ColorSchemeOptions;
	setMode: (variant: Partial<ColorSchemeOptions>) => void;
};

const storageColorSchemeKey = "prefers-color-scheme";
const hspColorKey = "hspColor";

const getPreferredTheme = (): PaletteMode => {
	const storageValue = localStorage.getItem(storageColorSchemeKey);
	if (storageValue) return storageValue === "dark" ? "dark" : "light";
	return window?.matchMedia("(prefers-color-scheme: dark)")?.matches
		? "dark"
		: "light";
};

const getHspColor = () => localStorage.getItem(hspColorKey) ?? undefined;

const ThemeModeContext = createContext<ThemeModeContextProps>(
	{} as ThemeModeContextProps,
);

const ColorSchemeProvider = ({
	children,
	getTheme,
	zIndexBase,
}: {
	children: ReactNode;
	getTheme: GetThemeFunctionType;
	zIndexBase?: number;
}) => {
	const [mode, setMode] = useState<ColorSchemeOptions>({
		paletteMode: getPreferredTheme(),
		hspColor: getHspColor(),
	});

	const setColorScheme = useCallback((options: Partial<ColorSchemeOptions>) => {
		if (options.hspColor) localStorage.setItem(hspColorKey, options.hspColor);
		if (options.paletteMode)
			localStorage.setItem(storageColorSchemeKey, options.paletteMode);
		setMode((a) => ({ ...a, ...options }));
	}, []);

	const theme = useMemo(
		() => getTheme(mode.paletteMode, mode.hspColor, zIndexBase),
		[mode.hspColor, mode.paletteMode, getTheme, zIndexBase],
	);

	const contextValue = useMemo(
		() => ({ mode, setMode: setColorScheme }),
		[mode, setColorScheme],
	);

	return (
		<ThemeModeContext.Provider value={contextValue}>
			<ThemeProvider theme={theme}>{children}</ThemeProvider>
		</ThemeModeContext.Provider>
	);
};

export default ColorSchemeProvider;

export const useColorScheme = () => useContext(ThemeModeContext);
